import Big from 'big.js';
import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { DownloadQuoteInvoice, OpenQuotes, QuoteRefQuery } from 'smg/customer-api/v2/quotes';
import { ShowError } from 'smg/errors';
import * as lineitems from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import * as formatting from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, IsPdfLike, Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML, TrimTrailingBreaks } from 'smg/richtext';
import * as template from 'smg/template';
import { DollarFormat, RemToPx } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';

const FALLBACK_SUBJECT = 'Your quote is ready';

const PROOF_SIZES = '(min-width: 1400px) 500px, (min-width: 1200px) 430px, (min-width: 992px) 360px, (min-width: 576px) 45vw, 100vw';

interface sendGroup {
    quoteSent?: models.QuoteSent;
    created?: DateTime;
    repFirstName: string;
    quotes: models.Quote[];
}

function dateFull(dt: DateTime): string {
    return dt.toLocaleString(DateTime.DATE_FULL);
}

function timeSimple(dt: DateTime): string {
    return dt.toLocaleString(DateTime.TIME_SIMPLE);
}

function sentLine(group: sendGroup): string {
    const firstName = group.repFirstName;
    if (!group.created) {
        return firstName ? `${firstName} built this for you.` : '';
    }
    const who = firstName || 'We';
    return `${who} sent this ${dateFull(group.created)} at ${timeSimple(group.created)}.`;
}

function badge(text: string, classes: string[], icon?: string): HTMLSpanElement {
    const $badge = document.createElement('span');
    $badge.classList.add('badge', ...classes);
    if (icon) {
        const $icon = document.createElement('i');
        $icon.className = `${icon} me-1`;
        $icon.setAttribute('aria-hidden', 'true');
        $badge.append($icon);
    }
    $badge.append(text);
    return $badge;
}

const neverEmailedBadge = () => badge('Never emailed', ['bg-white', 'border', 'text-secondary']);

function renderMessage($body: HTMLElement, quoteSent: models.QuoteSent): void {
    const content = quoteSent.content?.trim() ?? '';
    if (!content) {
        return;
    }
    let rendered: string;
    if (quoteSent.contentType === 'text/html') {
        rendered = SanitizeRichHTML(content);
    } else {
        rendered = html.Nl2br(html.Escape(content));
    }
    $body.innerHTML = Linkify(TrimTrailingBreaks(rendered));
    $body.classList.remove('d-none');
}

function renderAttachment(attachment: models.QuoteSentAttachment, fancyboxGroup: string): HTMLElement {
    const $el = template.Clone('#quote-attachment-template').firstElementChild as HTMLElement;
    const name = attachment.name.trim() || 'Attachment';
    const url = attachment.publicURL;
    const fileName = attachment.name || attachment.key;

    const $name = $el.querySelector('.quote-attachment-name') as HTMLElement;
    $name.textContent = name;
    $name.title = name;
    if (attachment.size) {
        ($el.querySelector('.quote-attachment-size') as HTMLElement).textContent = formatting.HumanFileSize(attachment.size, true).replace('.0 ', ' ');
    }

    const $download = $el.querySelector('.quote-attachment-download') as HTMLAnchorElement;
    $download.href = url;
    $download.setAttribute('download', name);
    $download.setAttribute('aria-label', `Download ${name}`);
    $download.title = `Download ${name}`;

    // Same verdict as the quote email (Upload.Renderable): an unknown extension is a download, not a broken image.
    const isImage = IsImageURL(fileName);
    const isPdf = !isImage && IsPdfLike(undefined, fileName);
    if (!isImage && !isPdf) {
        ($el.querySelector('.quote-attachment-nopreview') as HTMLElement).classList.remove('d-none');
        return $el;
    }

    const $preview = $el.querySelector('.quote-attachment-preview') as HTMLAnchorElement;
    $preview.classList.remove('d-none');
    $preview.href = url;
    $preview.setAttribute('data-fancybox', fancyboxGroup);
    $preview.setAttribute('data-caption', name);

    const $img = $preview.querySelector('img') as HTMLImageElement;
    $img.alt = name;
    if (isPdf) {
        void Picture($img, `${url}?format=web`, { displayWidth: 132, fancyboxThumb: true, pdfSrc: url });
    } else {
        void Picture($img, url, { displayWidth: 132, fancyboxThumb: true });
    }

    const $view = $el.querySelector('.quote-attachment-view') as HTMLAnchorElement;
    $view.classList.remove('d-none');
    $view.addEventListener('click', e => {
        e.preventDefault();
        $preview.click();
    });

    return $el;
}

function renderProof(quoteProof: models.QuoteProof, fancyboxGroup: string, eager: boolean): HTMLElement | null {
    const proof = quoteProof.proof;
    if (!proof) {
        return null;
    }
    const urls = proof.getURLs();
    if (!urls.main) {
        return null;
    }

    const $el = template.Clone('#quote-proof-template').firstElementChild as HTMLElement;
    const label = `Proof ${quoteProof.number ?? ''}`.trim();

    ($el.querySelector('.quote-proof-number') as HTMLElement).textContent = quoteProof.number ?? '';

    const $link = $el.querySelector('.quote-proof-link') as HTMLAnchorElement;
    $link.href = urls.main;
    $link.setAttribute('data-fancybox', fancyboxGroup);
    $link.setAttribute('data-caption', quoteProof.name || label);

    const $img = $link.querySelector('img') as HTMLImageElement;
    $img.alt = label;
    void Picture($img, urls.main, { displayWidth: 500, sizes: PROOF_SIZES, fancyboxThumb: true, pdfSrc: proof.url(), lazy: !eager });

    ($el.querySelector('.quote-proof-view') as HTMLAnchorElement).addEventListener('click', e => {
        e.preventDefault();
        $link.click();
    });

    const $name = $el.querySelector('.quote-proof-name') as HTMLElement;
    if (quoteProof.name) {
        $name.textContent = quoteProof.name;
    } else {
        $name.remove();
    }

    return $el;
}

interface proofFigure {
    quoteProof: models.QuoteProof;
    $figure: HTMLElement;
    label: string;
}

function proofCount(n: number): string {
    if (n === 1) {
        return '1 proof';
    }
    return `${n.toLocaleString()} proofs`;
}

function reducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function proofThumb(figure: proofFigure, displayWidth: number): HTMLButtonElement {
    const $button = template.Clone('#quote-proof-thumb-template').firstElementChild as HTMLButtonElement;
    $button.setAttribute('aria-label', figure.label);
    $button.title = figure.label;
    const proof = figure.quoteProof.proof as models.Proof;
    void Picture($button.querySelector('img') as HTMLImageElement, proof.getURLs().main, { displayWidth, pdfSrc: proof.url() });
    return $button;
}

// Every figure stays in the DOM so the lightbox group holds them all; the strip,
// the steps, each line's thumbnail and, with a mouse beside the sticky gallery, a
// line the pointer rests on only pick the visible one. Hover never scrolls, and
// the pick stays when the pointer leaves the lines.
function wireProofs(figures: proofFigure[], $gallery: HTMLElement, $lines: HTMLElement): void {
    const $thumbs = $gallery.querySelector('.quote-proof-thumbs') as HTMLElement;
    const $steps = $gallery.querySelector('.quote-proof-steps') as HTMLElement;
    const $prev = $steps.querySelector('.quote-proof-prev') as HTMLButtonElement;
    const $next = $steps.querySelector('.quote-proof-next') as HTMLButtonElement;
    const $tabs: HTMLButtonElement[] = [];
    const lineThumbs: { shown: number; $thumb: HTMLButtonElement }[] = [];
    const $proofLines = [...$lines.querySelectorAll<HTMLElement>('.quote-line[data-quote-proof-id]')];
    const hoverPicks = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 992px)');
    const split = window.matchMedia('(min-width: 992px)');
    let shown = -1;
    let flashTimer = 0;

    const count = proofCount(figures.length);
    ($gallery.querySelector('.quote-proof-count') as HTMLElement).textContent = count;

    const step = ($step: HTMLButtonElement, target: number) => {
        const off = target < 0 || target >= figures.length;
        $step.disabled = off;
        ($step.parentElement as HTMLElement).classList.toggle('disabled', off);
        const label = figures[off ? shown : target].label;
        $step.setAttribute('aria-label', label);
        $step.title = off ? '' : label;
    };

    const show = (next: number) => {
        if (next === shown) {
            return;
        }
        const previous = shown;
        shown = next;

        // Hold the outgoing height until the incoming image loads, so the card does not collapse.
        const $incomingLink = figures[next].$figure.querySelector('.quote-proof-link') as HTMLElement;
        const $incomingImg = $incomingLink.querySelector('img') as HTMLImageElement;
        if (previous >= 0 && !$incomingImg.complete) {
            const height = (figures[previous].$figure.querySelector('.quote-proof-link') as HTMLElement).getBoundingClientRect().height;
            $incomingLink.style.minHeight = `${height}px`;
            $incomingImg.addEventListener('load', () => {
                $incomingLink.style.minHeight = '';
            }, { once: true });
        }
        figures.forEach((figure, i) => figure.$figure.classList.toggle('d-none', i !== next));
        if (previous >= 0 && !reducedMotion()) {
            $incomingImg.animate(
                [{ opacity: 0, transform: 'scale(.985)' }, { opacity: 1, transform: 'scale(1)' }],
                { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' },
            );
        }

        $tabs.forEach(($tab, i) => {
            const on = i === next;
            $tab.classList.toggle('active', on);
            $tab.setAttribute('aria-selected', String(on));
            $tab.tabIndex = on ? 0 : -1;
        });
        for (const lineThumb of lineThumbs) {
            lineThumb.$thumb.classList.toggle('active', lineThumb.shown === next);
        }
        // The lines the new proof prices flash grey, so the customer sees which they are.
        window.clearTimeout(flashTimer);
        for (const $line of $proofLines) {
            const on = $line.dataset.quoteProofId === figures[next].quoteProof.id;
            const $badge = $line.querySelector('.quote-line-proof') as HTMLElement;
            $badge.classList.toggle('text-bg-primary', on);
            $badge.classList.toggle('text-white', on);
            $badge.classList.toggle('bg-white', !on);
            $badge.classList.toggle('border', !on);
            $badge.classList.toggle('text-primary-emphasis', !on);
            $line.classList.toggle('quote-line-flash', on && previous >= 0);
        }
        flashTimer = window.setTimeout(() => {
            $proofLines.forEach($line => $line.classList.remove('quote-line-flash'));
        }, 700);
        if (figures.length > 1) {
            step($prev, next - 1);
            step($next, next + 1);
        }
    };

    // From a line: show its proof, and scroll back up to the gallery when it is
    // out of view (stacked layouts). In the split the gallery is sticky beside the
    // lines, so nothing moves.
    const bring = (next: number) => {
        show(next);
        const headerHeight = document.querySelector<HTMLElement>('header')?.offsetHeight ?? 0;
        const top = $gallery.getBoundingClientRect().top;
        if (top < headerHeight || top > window.innerHeight * 0.6) {
            window.scrollTo({ top: window.scrollY + top - headerHeight - RemToPx(1), behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
        $gallery.focus({ preventScroll: true });
    };

    // From the strip: in the split, where the gallery stays beside the lines, bring
    // the proof's first line into view. Stacked, that would scroll the proof away.
    const reveal = (next: number) => {
        show(next);
        const $line = $proofLines.find($proofLine => $proofLine.dataset.quoteProofId === figures[next].quoteProof.id);
        if (!split.matches || !$line) {
            return;
        }
        const headerHeight = document.querySelector<HTMLElement>('header')?.offsetHeight ?? 0;
        const { top, bottom } = $line.getBoundingClientRect();
        if (top < headerHeight || bottom > window.innerHeight) {
            window.scrollTo({ top: window.scrollY + top - headerHeight - RemToPx(1), behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
    };

    for (const $line of $proofLines) {
        const index = figures.findIndex(figure => figure.quoteProof.id === $line.dataset.quoteProofId);
        if (index < 0) {
            continue;
        }
        const figure = figures[index];
        if (figure.label !== `Proof ${figure.quoteProof.number ?? ''}`.trim()) {
            const $name = $line.querySelector('.quote-line-proof-name') as HTMLElement;
            $name.textContent = figure.quoteProof.name ?? '';
            $name.classList.remove('d-none');
        }
        const $thumb = proofThumb(figure, 72);
        $thumb.addEventListener('click', () => bring(index));
        const $slot = $line.querySelector('.quote-line-thumb') as HTMLElement;
        $slot.appendChild($thumb);
        $slot.classList.remove('d-none');
        lineThumbs.push({ shown: index, $thumb });

        // A short rest, so a pointer crossing the lines on its way to Add to cart
        // does not pick every line it passes.
        $line.classList.add('quote-line-picks');
        let rest = 0;
        $line.addEventListener('pointerenter', e => {
            if (e.pointerType === 'mouse' && hoverPicks.matches) {
                rest = window.setTimeout(() => show(index), 120);
            }
        });
        $line.addEventListener('pointerleave', () => window.clearTimeout(rest));
        $line.addEventListener('click', e => {
            if (hoverPicks.matches && !(e.target as Element).closest('button, a')) {
                show(index);
            }
        });
    }

    if (figures.length > 1) {
        $thumbs.setAttribute('aria-label', count);
        figures.forEach((figure, i) => {
            const $tab = proofThumb(figure, 84);
            $tab.setAttribute('role', 'tab');
            const $number = $tab.querySelector('.quote-proof-thumb-number') as HTMLElement;
            $number.textContent = `Proof ${figure.quoteProof.number ?? ''}`.trim();
            $number.classList.remove('d-none');
            if (figure.label !== $number.textContent && figure.quoteProof.name) {
                const $name = $tab.querySelector('.quote-proof-thumb-name') as HTMLElement;
                $name.textContent = figure.quoteProof.name;
                $name.classList.remove('d-none');
            }
            $tab.addEventListener('click', () => reveal(i));
            $tab.addEventListener('keydown', e => {
                const last = figures.length - 1;
                let target: number;
                switch (e.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    target = i === last ? 0 : i + 1;
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    target = i === 0 ? last : i - 1;
                    break;
                case 'Home':
                    target = 0;
                    break;
                case 'End':
                    target = last;
                    break;
                default:
                    return;
                }
                e.preventDefault();
                show(target);
                $tabs[target].focus();
            });
            $thumbs.appendChild($tab);
            $tabs.push($tab);
        });
        $prev.addEventListener('click', () => show(shown - 1));
        $next.addEventListener('click', () => show(shown + 1));
        $thumbs.classList.remove('d-none');
        $steps.classList.remove('d-none');
    } else {
        $thumbs.remove();
        $steps.remove();
    }

    show(0);
}

async function renderOption(childLineItem: models.QuoteChildLineItem): Promise<HTMLElement> {
    const $el = template.Clone('#quote-option-template').firstElementChild as HTMLElement;

    const child = (await lineitems.ChildMap()).get(childLineItem.childLineItemID || '')?.networkName() || childLineItem.child || '';
    const option = (await lineitems.OptionMap()).get(childLineItem.childLineItemOptionID || '')?.networkName() || childLineItem.option || '';
    ($el.querySelector('.quote-option-name') as HTMLElement).textContent = [child, option].filter(Boolean).join(': ');

    const quantity = childLineItem.quantity || 0;
    const price = Big(childLineItem.price || 0);
    const extended = price.mul(quantity);
    const note = childLineItem.description?.trim() ?? '';

    if (extended.gt(0)) {
        const $math = $el.querySelector('.quote-option-math') as HTMLElement;
        $math.textContent = ` · ${quantity.toLocaleString()} × ${DollarFormat(price)}`;
        $math.classList.remove('d-none');
    }
    if (note) {
        const $note = $el.querySelector('.quote-option-note') as HTMLElement;
        $note.textContent = ` (${note})`;
        $note.classList.remove('d-none');
    }

    // $0.00 rows record what was chosen (a mold on file, a waived fee), so they stay.
    const $price = $el.querySelector('.quote-option-price') as HTMLElement;
    if (extended.gt(0)) {
        $price.textContent = DollarFormat(extended);
    } else if (/waiv/i.test(note)) {
        $price.textContent = 'Waived, $0.00';
        $price.classList.add('fst-italic', 'text-body-tertiary');
    } else {
        $price.textContent = DollarFormat(0);
        $price.classList.add('text-body-tertiary');
    }

    return $el;
}

// The customer payload ties a line to its proof by id only.
async function renderLine(lineItem: models.QuoteLineItem, proofNumbers: Map<string, string>): Promise<HTMLElement> {
    const $el = template.Clone('#quote-line-template').firstElementChild as HTMLElement;

    const proofNumber = lineItem.quoteProof?.number || proofNumbers.get(lineItem.quoteProofID ?? '') || '';
    if (proofNumber) {
        const $row = $el.querySelector('.quote-line-proof-row') as HTMLElement;
        ($row.querySelector('.quote-line-proof span') as HTMLElement).textContent = proofNumber;
        $row.classList.remove('d-none');
        $el.dataset.quoteProofId = lineItem.quoteProofID || lineItem.quoteProof?.id || '';
    }

    const quantity = lineItem.quantity || 0;
    const product = (await lineitems.LineItemMap()).get(lineItem.lineItemID || '')?.networkNamePlural() || lineItem.lineItem || '';
    const type = (await lineitems.TypeMap()).get(lineItem.lineItemTypeID || '')?.networkName() || lineItem.type || '';
    const size = (await lineitems.SizeMap()).get(lineItem.lineItemTypeSizeID || '')?.networkName() || lineItem.size || '';
    ($el.querySelector('.quote-line-name') as HTMLElement).textContent =
        `${quantity.toLocaleString()} × ${[size, type, product].filter(Boolean).join(' ')}`;

    const description = lineItem.description?.trim() ?? '';
    if (description) {
        const $description = $el.querySelector('.quote-line-description') as HTMLElement;
        $description.textContent = `“${description}”`;
        $description.classList.remove('d-none');
    }

    const price = Big(lineItem.price || 0);
    ($el.querySelector('.quote-line-quantity') as HTMLElement).textContent = quantity.toLocaleString();
    ($el.querySelector('.quote-line-price') as HTMLElement).textContent = DollarFormat(price);
    ($el.querySelector('.quote-line-subtotal') as HTMLElement).textContent = DollarFormat(price.mul(quantity));

    const $options = $el.querySelector('.quote-line-options') as HTMLElement;
    for (const childLineItem of lineItem.quoteChildLineItems) {
        $options.appendChild(await renderOption(childLineItem));
    }

    return $el;
}

function hasMold(quote: models.Quote): boolean {
    return quote.quoteLineItems.some(lineItem => lineItem.quoteChildLineItems.some(childLineItem =>
        childLineItem.childLineItem?.mold || /mold/i.test(childLineItem.child ?? '')));
}

async function downloadPDF(quote: models.Quote, $button: HTMLButtonElement): Promise<void> {
    const $label = $button.querySelector('span') as HTMLElement;
    const idle = $label.textContent;
    buttons.Start($button);
    $button.disabled = true;
    $label.textContent = 'Preparing PDF';
    try {
        await DownloadQuoteInvoice(QuoteRefQuery(quote.key || quote.id || ''));
    } catch (err) {
        ShowError(err);
    } finally {
        buttons.Stop($button);
        $button.disabled = false;
        $label.textContent = idle;
    }
}

async function renderQuote(quote: models.Quote, group: sendGroup, singleQuoteOnPage: boolean): Promise<HTMLElement> {
    const $el = template.Clone('#quote-template').firstElementChild as HTMLElement;

    $el.querySelectorAll('.quote-serial').forEach($serial => {
        $serial.textContent = quote.serial ?? '';
    });

    const project = quote.quoteRequest?.quoteRequestProject?.name?.trim() ?? '';
    if (project) {
        const $project = $el.querySelector('.quote-project') as HTMLElement;
        $project.textContent = project;
        $project.classList.remove('d-none');
    }

    const badges: HTMLElement[] = [];
    if (!group.quoteSent) {
        badges.push(neverEmailedBadge());
    }
    const order = quote.quoteOrders[0]?.order;
    if (order) {
        badges.push(badge('Ordered', ['bg-success-subtle', 'text-success-emphasis'], 'bi bi-check-lg'));
        const $ordered = $el.querySelector('.quote-ordered') as HTMLElement;
        ($ordered.querySelector('.quote-order-number') as HTMLElement).textContent = String(order.invoiceNumber ?? '');
        ($ordered.querySelector('.quote-order-date') as HTMLElement).textContent = order.created?.isValid ? dateFull(order.created) : '';
        $ordered.classList.remove('d-none');
    }
    const dateNeeded = quote.quoteRequest?.dateNeeded;
    if (dateNeeded?.isValid && dateNeeded.year > 1) {
        badges.push(badge(`Needed by ${dateFull(dateNeeded)}`, ['bg-primary-subtle', 'text-primary-emphasis']));
    }
    if (badges.length) {
        const $badges = $el.querySelector('.quote-badges') as HTMLElement;
        $badges.append(...badges);
        $badges.classList.remove('d-none');
    }

    const $proofsCol = $el.querySelector('.quote-proofs-col') as HTMLElement;
    const $gallery = $el.querySelector('.quote-gallery') as HTMLElement;
    const $proofs = $gallery.querySelector('.quote-proofs') as HTMLElement;
    const $lines = $el.querySelector('.quote-lines') as HTMLElement;
    const $linesCol = $lines.parentElement as HTMLElement;
    const $actions = $el.querySelector('.quote-actions') as HTMLElement;
    const $revisionsCol = $el.querySelector('.quote-request-revision-col') as HTMLElement;
    const $revisions = $el.querySelector('.quote-request-revision') as HTMLAnchorElement;

    const proofNumbers = new Map<string, string>();
    for (const quoteProof of quote.quoteProofs) {
        if (quoteProof.id && quoteProof.number) {
            proofNumbers.set(quoteProof.id, quoteProof.number);
        }
    }
    for (const lineItem of quote.quoteLineItems) {
        $lines.appendChild(await renderLine(lineItem, proofNumbers));
    }

    const fancyboxGroup = `quote-${quote.id ?? quote.serial ?? ''}`;
    const figures: proofFigure[] = [];
    // Hidden figures never enter the viewport, so lazy loading would leave them unfetched until tapped.
    const eager = quote.quoteProofs.length > 1;
    for (const quoteProof of quote.quoteProofs) {
        const $figure = renderProof(quoteProof, fancyboxGroup, eager);
        if ($figure) {
            figures.push({ quoteProof, $figure, label: '' });
        }
    }
    // Proof numbers follow each design's own revisions, so three designs can all be
    // Proof 1; the file name tells those apart.
    for (const figure of figures) {
        const number = figure.quoteProof.number ?? '';
        const shared = figures.some(other => other !== figure && other.quoteProof.number === number);
        figure.label = `Proof ${number}`.trim();
        if (shared && figure.quoteProof.name) {
            figure.label += `, ${figure.quoteProof.name}`;
        }
        (figure.$figure.querySelector('.quote-proof-link img') as HTMLImageElement).alt = figure.label;
    }
    // The strip and the lightbox run in the order the quoted lines name their
    // proofs, so the two read side by side; a proof no line names goes last.
    const lineOrder = new Map<string, number>();
    quote.quoteLineItems.forEach((lineItem, i) => {
        const quoteProofID = lineItem.quoteProofID || lineItem.quoteProof?.id || '';
        if (quoteProofID && !lineOrder.has(quoteProofID)) {
            lineOrder.set(quoteProofID, i);
        }
    });
    const unnamed = quote.quoteLineItems.length;
    figures.sort((a, b) => (lineOrder.get(a.quoteProof.id ?? '') ?? unnamed) - (lineOrder.get(b.quoteProof.id ?? '') ?? unnamed));
    if (figures.length) {
        $proofs.append(...figures.map(figure => figure.$figure));
        wireProofs(figures, $gallery, $lines);
        $revisions.href = `${Relref('/revisions')}?id=${quote.key}`;
    } else {
        $gallery.remove();
        $proofsCol.classList.remove('col-lg-5');
        $linesCol.classList.remove('col-lg-7');
        ($el.querySelector('.quote-no-proof') as HTMLElement).classList.remove('d-none');
        $revisionsCol.remove();
        $actions.classList.remove('row-cols-sm-2');
    }

    ($el.querySelector('.quote-total') as HTMLElement).textContent = DollarFormat(quote.cachedTotal);
    if (hasMold(quote)) {
        ($el.querySelector('.quote-mold') as HTMLElement).classList.remove('d-none');
    }

    const $download = $el.querySelector('.quote-invoice-download') as HTMLButtonElement;
    $download.addEventListener('click', () => {
        void downloadPDF(quote, $download);
    });

    const $addToCart = $el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
    const $addToCartLabel = $addToCart.querySelector('span') as HTMLElement;
    const $addToCartIcon = $addToCart.querySelector('i') as HTMLElement;
    $addToCart.addEventListener('click', async () => {
        buttons.Start($addToCart);
        $addToCartIcon.classList.add('d-none');
        $addToCartLabel.textContent = 'Adding';
        try {
            await carts.AddCartItem({ quoteID: quote.key }, { promptToCheckout: !singleQuoteOnPage });
            buttons.Stop($addToCart);
            $addToCart.classList.remove('btn-primary');
            $addToCart.classList.add('btn-outline-success');
            $addToCartIcon.classList.remove('bi-cart-fill');
            $addToCartIcon.classList.add('bi-check-lg');
            $addToCartLabel.textContent = 'Added';
            $addToCart.setAttribute('disabled', 'disabled');
            if (singleQuoteOnPage) {
                window.location.href = Relref('/cart');
            }
        } catch (err) {
            buttons.Stop($addToCart);
            $addToCartLabel.textContent = 'Add to cart';
            throw err;
        } finally {
            $addToCartIcon.classList.remove('d-none');
        }
    });

    return $el;
}

async function renderSend(group: sendGroup, latest: boolean, singleQuoteOnPage: boolean): Promise<HTMLElement> {
    const $el = template.Clone('#quote-send-template').firstElementChild as HTMLElement;
    const quoteSent = group.quoteSent;
    const firstName = group.repFirstName;

    const $kicker = $el.querySelector('.quote-send-kicker') as HTMLElement;
    if (firstName) {
        ($el.querySelector('.quote-send-rep') as HTMLElement).textContent = firstName;
    } else {
        $kicker.remove();
    }
    if (!latest || !quoteSent) {
        $kicker.classList.replace('text-primary', 'text-muted');
    }

    const $badge = $el.querySelector('.quote-send-badge') as HTMLElement;
    if (!quoteSent) {
        $badge.replaceWith(neverEmailedBadge());
    } else if (!latest) {
        $badge.textContent = 'Earlier send';
        $badge.classList.add('bg-secondary-subtle', 'text-secondary-emphasis');
        $badge.classList.remove('d-none');
        $el.classList.add('quote-send-earlier');
    }

    ($el.querySelector('.quote-send-subject') as HTMLElement).textContent = quoteSent?.subject?.trim() || FALLBACK_SUBJECT;

    const $line = $el.querySelector('.quote-send-line') as HTMLElement;
    const line = sentLine(group);
    if (line) {
        $line.textContent = line;
    } else {
        $line.remove();
    }

    if (quoteSent) {
        renderMessage($el.querySelector('.quote-send-message') as HTMLElement, quoteSent);

        const attachments = quoteSent.attachments.filter(attachment => attachment.publicURL);
        if (attachments.length) {
            const $attachments = $el.querySelector('.quote-send-attachments') as HTMLElement;
            const $list = $attachments.querySelector('.quote-send-attachment-list') as HTMLElement;
            const fancyboxGroup = `quote-sent-${quoteSent.quoteSentInstanceID || quoteSent.id || ''}`;
            for (const attachment of attachments) {
                $list.appendChild(renderAttachment(attachment, fancyboxGroup));
            }
            $attachments.classList.remove('d-none');
        }
    }

    if (group.quotes.length > 1) {
        ($el.querySelector('.quote-send-several') as HTMLElement).classList.remove('d-none');
    }

    const $quotes = $el.querySelector('.quote-send-quotes') as HTMLElement;
    for (const quote of group.quotes) {
        $quotes.appendChild(await renderQuote(quote, group, singleQuoteOnPage));
    }

    return $el;
}

function groupBySend(quotes: models.Quote[]): sendGroup[] {
    const groups = new Map<string, sendGroup>();
    const unsent: sendGroup = { quotes: [], repFirstName: '' };

    for (const quote of quotes) {
        let latestQuoteSent: models.QuoteSent | undefined;
        let latestCreated: DateTime | undefined;
        for (const quoteSent of quote.quoteSents) {
            const created = quoteSent.created;
            if (!created?.isValid) {
                continue;
            }
            if (!latestCreated || created.toMillis() > latestCreated.toMillis()) {
                latestQuoteSent = quoteSent;
                latestCreated = created;
            }
        }

        if (!latestQuoteSent || !latestCreated) {
            unsent.quotes.push(quote);
            continue;
        }

        const key = latestQuoteSent.quoteSentInstanceID || latestQuoteSent.id || '';
        let group = groups.get(key);
        if (!group) {
            group = { quotes: [], repFirstName: '' };
            groups.set(key, group);
        }
        group.quotes.push(quote);
        if (!group.created || latestCreated.toMillis() > group.created.toMillis()) {
            group.quoteSent = latestQuoteSent;
            group.created = latestCreated;
            group.repFirstName = latestQuoteSent.user?.firstName.trim() ?? '';
        }
    }

    const sorted = [...groups.values()].sort((a, b) => (b.created?.toMillis() ?? 0) - (a.created?.toMillis() ?? 0));
    if (unsent.quotes.length) {
        unsent.repFirstName = sorted[0]?.repFirstName ?? '';
        sorted.push(unsent);
    }
    return sorted;
}

function main() {
    const $spinner = document.querySelector('#quote-spinner') as HTMLElement;
    const $failed = document.querySelector('#quote-failed') as HTMLElement;
    const $lookup = document.querySelector('#quote-lookup') as HTMLElement;
    const $lookupEmpty = document.querySelector('#quote-lookup-empty') as HTMLElement;
    const $search = document.querySelector('#quote-search') as HTMLInputElement;
    const $searchSubmit = document.querySelector('#quote-search-submit') as HTMLButtonElement;
    const $results = document.querySelector('#quote-results') as HTMLElement;
    const $resultsList = document.querySelector('#quote-results-list') as HTMLElement;
    const $page = document.querySelector('#quote-page') as HTMLElement;
    const $back = document.querySelector('#quote-back') as HTMLElement;
    const $sends = document.querySelector('#quote-sends') as HTMLElement;

    // The proofs sit under the sticky header, whose height changes with banners and breakpoints.
    const $header = document.querySelector<HTMLElement>('header');
    if ($header) {
        new ResizeObserver(() => {
            $page.style.setProperty('--header-height', `${$header.offsetHeight}px`);
        }).observe($header);
    }

    const query = QueryString().get('q') ?? '';
    const quoteID = QueryString().get('id');
    const quoteSentInstanceID = QueryString().get('instance');

    $lookup.querySelector('form')?.addEventListener('submit', () => {
        buttons.Start($searchSubmit);
        $searchSubmit.querySelector('i')?.classList.add('d-none');
        ($searchSubmit.querySelector('span') as HTMLElement).textContent = 'Searching';
    });

    const show = ($view: HTMLElement) => {
        for (const $other of [$spinner, $failed, $lookup, $results, $page]) {
            $other.classList.toggle('d-none', $other !== $view);
        }
    };

    const showLookup = (empty: boolean) => {
        $search.value = query;
        $lookupEmpty.classList.toggle('d-none', !empty);
        show($lookup);
    };

    let q = query;
    if (quoteID) {
        q = quoteID.length === 43 ? `key:${quoteID}` : `id:${quoteID}`;
    } else if (quoteSentInstanceID) {
        q = quoteSentInstanceID.length === 43 ? `quoteSentKey:${quoteSentInstanceID}` : `quoteSentInstanceID:${quoteSentInstanceID}`;
    } else if (!q) {
        showLookup(false);
        return;
    }

    OpenQuotes({
        sort: 'created asc',
        max: 100,
        data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,quoteSents',
        p: 0,
        q,
    }).then(async data => {
        if (!data.results.length) {
            showLookup(Boolean(query));
            return;
        }

        const groups = groupBySend(data.results);

        const showDetail = async (detailGroups: sendGroup[], fromResults: boolean) => {
            show($spinner);
            $sends.replaceChildren();
            const quoteCount = detailGroups.reduce((n, group) => n + group.quotes.length, 0);
            for (const [i, group] of detailGroups.entries()) {
                $sends.appendChild(await renderSend(group, i === 0, quoteCount === 1));
            }
            $back.classList.toggle('d-none', !fromResults);
            show($page);
        };

        if (!query) {
            await showDetail(groups, false);
            return;
        }

        for (const group of groups) {
            for (const quote of group.quotes) {
                const $row = template.Clone('#quote-result-template').firstElementChild as HTMLElement;
                ($row.querySelector('.quote-result-serial') as HTMLElement).textContent = quote.serial ?? '';
                const project = quote.quoteRequest?.quoteRequestProject?.name?.trim() ?? '';
                ($row.querySelector('.quote-result-line') as HTMLElement).textContent =
                    [project, group.quoteSent ? sentLine(group) : 'Never emailed'].filter(Boolean).join(' · ');
                ($row.querySelector('.quote-result-total') as HTMLElement).textContent = DollarFormat(quote.cachedTotal);
                ($row.querySelector('.quote-result-open') as HTMLButtonElement).addEventListener('click', () => {
                    void showDetail([group], true);
                    window.scrollTo({ top: 0 });
                });
                $resultsList.appendChild($row);
            }
        }
        $back.querySelector('a')?.addEventListener('click', e => {
            e.preventDefault();
            show($results);
            window.scrollTo({ top: 0 });
        });
        show($results);
    }).catch(err => {
        show($failed);
        throw err;
    });

    void lineitems.GetLineItems();
}

main();
